import React from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Icon,
  Link,
  SimpleGrid,
  Spinner,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from '@chakra-ui/react';
import { MdAttachFile, MdImage, MdOpenInNew, MdReceiptLong, MdShoppingCart } from 'react-icons/md';

type PurchaseLine = {
  id: number;
  Articulo?: string | null;
  Descripcion?: string | null;
  Cantidad?: number | null;
  Precio?: number | null;
  Total?: number | null;
};

type PurchaseDetail = {
  id: number;
  TipoCuenta?: string | null;
  Total?: number | null;
  Fecha_Registro?: string | null;
  createdAt?: string | null;
  Factura?: string | null;
  XML?: string | null;
  Evidencia?: string | null;
  Provedor?: {
    RazonSocial?: string | null;
    CodigoPostal?: string | null;
    RFC?: string | null;
  } | null;
  Compra_Lineas?: PurchaseLine[];
};

export default function PurchaseDetailView(props: { id: number }) {
  const { id } = props;
  const [data, setData] = React.useState<PurchaseDetail | null>(null);
  const [loading, setLoading] = React.useState(true);
  const cardBg = useColorModeValue('gray.50', 'navy.800');
  const borderColor = useColorModeValue('gray.100', 'whiteAlpha.100');
  const textColor = useColorModeValue('navy.700', 'white');
  const subtle = useColorModeValue('gray.500', 'gray.400');

  React.useEffect(() => {
    const fetchData = async () => {
      try {
        const formData = new FormData();
        formData.append('id', String(id));
        const response = await fetch(process.env.REACT_APP_URL_SERVER + '/una_compra', {
          method: 'POST',
          body: formData,
          credentials: 'include',
        });
        const result = await response.json();
        setData(result);
      } catch (error) {
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  if (loading) {
    return (
      <Flex minH="320px" align="center" justify="center">
        <Spinner />
      </Flex>
    );
  }

  if (!data) {
    return (
      <Flex minH="280px" align="center" justify="center">
        <Text color={subtle}>No se pudo cargar el detalle de la compra.</Text>
      </Flex>
    );
  }

  const purchaseDate = data.Fecha_Registro || data.createdAt;
  const attachments = [
    { key: 'evidencia', label: 'Evidencia', icon: MdImage, url: data.Evidencia, color: 'purple' },
    { key: 'factura', label: 'Factura', icon: MdReceiptLong, url: data.Factura, color: 'blue' },
    { key: 'xml', label: 'XML / Documento', icon: MdAttachFile, url: data.XML, color: 'teal' },
  ];

  return (
    <Stack spacing={6}>
      <Flex
        borderRadius="24px"
        bg="linear-gradient(135deg, #0f766e 0%, #14b8a6 100%)"
        px={{ base: 5, md: 6 }}
        py={{ base: 5, md: 6 }}
        color="white"
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={4}
      >
        <Stack spacing={2}>
          <HStack spacing={3}>
            <Icon as={MdShoppingCart} w={6} h={6} />
            <Text fontSize="xl" fontWeight="800">Compra #{data.id}</Text>
          </HStack>
          <Text fontSize="sm" color="whiteAlpha.800">
            {purchaseDate ? new Date(purchaseDate).toLocaleString('es-MX', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Fecha no disponible'}
          </Text>
        </Stack>
        <Stack spacing={2} align={{ base: 'flex-start', md: 'flex-end' }}>
          <Badge colorScheme="blackAlpha" px={3} py={1} borderRadius="full" fontSize="sm">
            {data.TipoCuenta || 'Sin tipo'}
          </Badge>
          <Text fontSize={{ base: '2xl', md: '3xl' }} fontWeight="900">
            ${(Number(data.Total || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Text>
        </Stack>
      </Flex>

      <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={5}>
        <InfoCard title="Proveedor" bg={cardBg} borderColor={borderColor}>
          <DataRow label="Razon social" value={data.Provedor?.RazonSocial || 'No registrada'} textColor={textColor} subtle={subtle} />
          <DataRow label="RFC" value={data.Provedor?.RFC || 'No registrado'} textColor={textColor} subtle={subtle} />
          <DataRow label="Codigo postal" value={data.Provedor?.CodigoPostal || 'No registrado'} textColor={textColor} subtle={subtle} />
        </InfoCard>

        <InfoCard title="Datos de la compra" bg={cardBg} borderColor={borderColor}>
          <DataRow label="ID" value={String(data.id)} textColor={textColor} subtle={subtle} />
          <DataRow label="Tipo de gasto" value={data.TipoCuenta || 'Sin tipo'} textColor={textColor} subtle={subtle} />
          <DataRow
            label="Fecha de registro"
            value={purchaseDate ? new Date(purchaseDate).toLocaleString('es-MX') : 'No disponible'}
            textColor={textColor}
            subtle={subtle}
          />
          <DataRow
            label="Total registrado"
            value={`$${(Number(data.Total || 0)).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            textColor={textColor}
            subtle={subtle}
          />
        </InfoCard>
      </SimpleGrid>

      <InfoCard title="Documentos y evidencias" bg={cardBg} borderColor={borderColor}>
        <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
          {attachments.map((item) => (
            <Box
              key={item.key}
              border="1px solid"
              borderColor={borderColor}
              borderRadius="20px"
              p={4}
              bg="white"
            >
              <HStack spacing={3} mb={3}>
                <Flex w="42px" h="42px" borderRadius="14px" bg={`${item.color}.50`} align="center" justify="center">
                  <Icon as={item.icon} color={`${item.color}.500`} w={5} h={5} />
                </Flex>
                <Stack spacing={0}>
                  <Text fontWeight="700" color={textColor}>{item.label}</Text>
                  <Text fontSize="xs" color={subtle}>{item.url ? 'Disponible' : 'No cargado'}</Text>
                </Stack>
              </HStack>
              {item.url ? (
                <Button
                  size="sm"
                  w="100%"
                  rightIcon={<Icon as={MdOpenInNew} />}
                  onClick={() => window.open(item.url as string, '_blank')}
                >
                  Abrir archivo
                </Button>
              ) : (
                <Text fontSize="sm" color={subtle}>No se adjunto este documento.</Text>
              )}
            </Box>
          ))}
        </SimpleGrid>

        {data.Evidencia ? (
          <Box mt={5}>
            <Text fontSize="sm" fontWeight="700" color={textColor} mb={3}>Vista previa de evidencia</Text>
            <Box
              as="img"
              src={data.Evidencia}
              alt={`Evidencia de compra ${data.id}`}
              w="100%"
              maxH="360px"
              objectFit="cover"
              borderRadius="22px"
              border="1px solid"
              borderColor={borderColor}
            />
          </Box>
        ) : null}
      </InfoCard>

      <InfoCard title="Conceptos registrados" bg={cardBg} borderColor={borderColor}>
        {data.Compra_Lineas && data.Compra_Lineas.length > 0 ? (
          <Table variant="simple">
            <Thead>
              <Tr>
                <Th borderColor={borderColor}>Articulo</Th>
                <Th borderColor={borderColor}>Descripcion</Th>
                <Th borderColor={borderColor}>Cantidad</Th>
                <Th borderColor={borderColor}>Precio</Th>
                <Th borderColor={borderColor}>Total</Th>
              </Tr>
            </Thead>
            <Tbody>
              {data.Compra_Lineas.map((line) => (
                <Tr key={line.id}>
                  <Td borderColor={borderColor}>
                    <Text color={textColor} fontWeight="700">{line.Articulo || 'Sin articulo'}</Text>
                  </Td>
                  <Td borderColor={borderColor}>
                    <Text color={subtle}>{line.Descripcion || 'Sin descripcion'}</Text>
                  </Td>
                  <Td borderColor={borderColor}>{Number(line.Cantidad || 0)}</Td>
                  <Td borderColor={borderColor}>${Number(line.Precio || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Td>
                  <Td borderColor={borderColor}>
                    <Text color={textColor} fontWeight="700">
                      ${Number(line.Total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        ) : (
          <Text color={subtle}>No hay lineas registradas para esta compra.</Text>
        )}
      </InfoCard>
    </Stack>
  );
}

function InfoCard(props: { title: string; children: React.ReactNode; bg: string; borderColor: string }) {
  const { title, children, bg, borderColor } = props;
  const textColor = useColorModeValue('navy.700', 'white');
  return (
    <Box border="1px solid" borderColor={borderColor} borderRadius="24px" bg={bg} p={5}>
      <Text fontSize="lg" fontWeight="800" color={textColor} mb={4}>
        {title}
      </Text>
      {children}
    </Box>
  );
}

function DataRow(props: { label: string; value: string; textColor: string; subtle: string }) {
  const { label, value, textColor, subtle } = props;
  return (
    <Grid templateColumns="150px 1fr" gap={3} py={2}>
      <Text fontSize="sm" color={subtle}>{label}</Text>
      <Text fontSize="sm" color={textColor} fontWeight="600">{value}</Text>
    </Grid>
  );
}
